import { Group, Burger, Text, Menu, ActionIcon, Box } from '@mantine/core';
import {
  IconSettings,
  IconLogout,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconCheck,
  IconContainer,
} from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLogout } from '@/hooks/useLogout';
import { useTank } from '@/contexts/TankContext';
import { TaskNotificationIcon } from '@/features/tasks/TaskNotificationIcon';

interface AppHeaderProps {
  mobileOpened: boolean;
  toggleMobile: () => void;
  toggleDesktop: () => void;
  desktopOpened: boolean;
}

export function AppHeader({ mobileOpened, toggleMobile, toggleDesktop, desktopOpened }: AppHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const { handleLogout } = useLogout();
  const { tanks, selectedTank, selectTank, isInitialized, error } = useTank();

  const handleSettings = () => {
    navigate('/settings');
  };

  // Tank switching lives in the gear menu, not the header bar: most users have
  // a single tank, so it doesn't warrant primary-nav placement, and inline it
  // crowded out the wordmark on mobile. Hidden entirely until the tank fetch
  // settles (or on error) — the gear menu still works without it.
  const showTankSection = isInitialized && !error && tanks.length > 0;

  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap" data-testid="app-header">
      <Group gap="sm" wrap="nowrap" miw={0}>
        <Burger
          opened={mobileOpened}
          onClick={toggleMobile}
          hiddenFrom="md"
          size="sm"
          aria-label="Toggle navigation"
        />
        <ActionIcon
          variant="subtle"
          color="teal"
          onClick={toggleDesktop}
          visibleFrom="md"
          aria-label="Toggle sidebar"
        >
          {desktopOpened ? <IconLayoutSidebarLeftCollapse size={20} /> : <IconLayoutSidebarLeftExpand size={20} />}
        </ActionIcon>
        <Text
          component={Link}
          to="/"
          size="lg"
          fw={700}
          c="var(--rdh-brand-accent)"
          style={{ letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0, textDecoration: 'none' }}
          aria-label="Reef DataHub home"
        >
          Reef DataHub
        </Text>
      </Group>

      <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
        <TaskNotificationIcon />

        <Menu shadow="md" width={220} position="bottom-end">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="teal"
            size="lg"
            aria-label="Settings menu"
          >
            <IconSettings size={22} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {showTankSection && (
            <>
              <Menu.Label>Tank</Menu.Label>
              {tanks.map((tank) => (
                <Menu.Item
                  key={tank.id}
                  onClick={() => selectTank(tank.id)}
                  styles={{ itemLabel: { minWidth: 0 } }}
                  leftSection={
                    tank.id === selectedTank?.id ? (
                      <IconCheck size={14} data-testid="selected-tank-check" aria-hidden />
                    ) : (
                      <Box w={14} />
                    )
                  }
                >
                  <Text size="sm" truncate maw={160}>
                    {tank.name}
                  </Text>
                </Menu.Item>
              ))}
              <Menu.Item
                component={Link}
                to="/tanks"
                leftSection={<IconContainer size={16} />}
              >
                Manage tanks
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          <Menu.Item
            leftSection={<IconSettings size={16} />}
            onClick={handleSettings}
          >
            Settings
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconLogout size={16} />}
            onClick={handleLogout}
            color="red"
          >
            Logout
          </Menu.Item>
        </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
